import jsPDF from 'jspdf';
import { format, parseISO } from 'date-fns';
import { STATUS_LABELS, AuthorizationStatus, SUBMISSION_LANE_LABELS, SubmissionLane } from '@/lib/authorization/types';
import { DENIAL_CATEGORY_LABELS, DenialCategory } from '@/lib/normalization/denialNormalization';
import { NORMALIZED_STATUS_LABELS, PayerNormalizedStatus } from '@/lib/normalization/statusNormalization';
import type { AuthorizationRequest, CaseDocument, CaseFollowUp, ActivityLogEntry } from '@/lib/authorization/types';

export interface EvidenceBundleData {
  caseData: AuthorizationRequest;
  documents: CaseDocument[];
  followUps: CaseFollowUp[];
  activityLog: ActivityLogEntry[];
  statusHistory: Array<{
    id: string;
    rawStatus: string | null;
    normalizedStatus: PayerNormalizedStatus;
    source: string;
    recordedAt: Date;
    recordedBy: string | null;
    notes: string | null;
  }>;
  overrideInfo?: {
    reason: string;
    overriddenBy: string;
    overriddenAt: string;
  };
}

interface PDFGenerationOptions {
  includeDocumentLinks?: boolean;
  includeFullTimeline?: boolean;
}

const LINK_OPS_METADATA = {
  generator: 'LINK Ops Authorization Management System',
  version: '1.0.0',
};

// Utility to create patient initials for PHI minimization
function getPatientInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.charAt(0)?.toUpperCase() || 'X';
  const last = lastName?.charAt(0)?.toUpperCase() || 'X';
  return `${first}.${last}.`;
}

// Format date safely
function safeFormatDate(dateString?: string | null, formatStr: string = 'MMM d, yyyy'): string {
  if (!dateString) return 'N/A';
  try {
    return format(parseISO(dateString), formatStr);
  } catch {
    return 'N/A';
  }
}

export function generateEvidenceBundlePDF(
  data: EvidenceBundleData,
  options: PDFGenerationOptions = {}
): jsPDF {
  const { caseData, documents, followUps, activityLog, statusHistory, overrideInfo } = data;
  const { includeDocumentLinks = true, includeFullTimeline = true } = options;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let yPos = margin;

  // Helper for adding text with word wrap
  const addText = (text: string, fontSize: number = 10, isBold: boolean = false) => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    const lines = doc.splitTextToSize(text, contentWidth);
    
    // Check if we need a new page
    if (yPos + lines.length * (fontSize * 0.4) > doc.internal.pageSize.getHeight() - margin) {
      doc.addPage();
      yPos = margin;
    }
    
    doc.text(lines, margin, yPos);
    yPos += lines.length * (fontSize * 0.4) + 3;
  };

  const addSection = (title: string) => {
    yPos += 5;
    if (yPos > doc.internal.pageSize.getHeight() - 40) {
      doc.addPage();
      yPos = margin;
    }
    doc.setFillColor(30, 41, 59); // slate-800
    doc.rect(margin, yPos - 5, contentWidth, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(title, margin + 3, yPos);
    doc.setTextColor(0, 0, 0);
    yPos += 10;
  };

  const addKeyValue = (key: string, value: string) => {
    if (yPos > doc.internal.pageSize.getHeight() - margin) {
      doc.addPage();
      yPos = margin;
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${key}:`, margin, yPos);
    doc.setFont('helvetica', 'normal');
    const valueLines = doc.splitTextToSize(value, contentWidth - 60);
    doc.text(valueLines, margin + 60, yPos);
    yPos += valueLines.length * 4 + 2;
  };

  // ========== HEADER ==========
  doc.setFillColor(15, 23, 42); // slate-900
  doc.rect(0, 0, pageWidth, 35, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Evidence Bundle Export', margin, 15);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Case: ${caseData.case_number}`, margin, 25);
  doc.text(`Generated: ${format(new Date(), 'MMM d, yyyy h:mm a')}`, pageWidth - margin - 60, 25);
  doc.setTextColor(0, 0, 0);
  yPos = 45;

  // ========== CASE SUMMARY ==========
  addSection('CASE SUMMARY');
  addKeyValue('Case Number', caseData.case_number);
  addKeyValue('Patient', `${getPatientInitials(caseData.patient?.first_name, caseData.patient?.last_name)} (MRN: ${caseData.patient?.mrn || 'N/A'})`);
  addKeyValue('Date of Birth', safeFormatDate(caseData.patient?.dob));
  addKeyValue('Status', STATUS_LABELS[caseData.status as AuthorizationStatus] || caseData.status);
  addKeyValue('Urgency', caseData.urgency || 'Routine');
  addKeyValue('Created', safeFormatDate(caseData.created_at, 'MMM d, yyyy h:mm a'));
  if (caseData.scheduled_date) {
    addKeyValue('Scheduled Date', safeFormatDate(caseData.scheduled_date));
  }

  // ========== PAYER & COVERAGE ==========
  addSection('PAYER & COVERAGE');
  addKeyValue('Payer', caseData.payer?.name || 'N/A');
  addKeyValue('Payer ID', caseData.payer?.payer_id || 'N/A');
  if (caseData.patient?.member_id) {
    addKeyValue('Member ID', caseData.patient.member_id);
  }
  if (caseData.patient?.group_number) {
    addKeyValue('Group Number', caseData.patient.group_number);
  }

  // ========== CLINICAL DETAILS ==========
  addSection('CLINICAL DETAILS');
  addKeyValue('CPT Codes', caseData.cpt_codes.join(', ') || 'None');
  if (caseData.modifiers?.length) {
    addKeyValue('Modifiers', caseData.modifiers.join(', '));
  }
  addKeyValue('Diagnoses (ICD-10)', caseData.icd10_codes.join(', ') || 'None');
  addKeyValue('Diagnosis Summary', caseData.diagnosis_summary || 'N/A');
  addKeyValue('Procedure Description', caseData.procedure_description || 'N/A');
  if (caseData.facility) {
    addKeyValue('Site of Service', `${caseData.facility.name} (${caseData.facility.type})`);
  }
  if (caseData.submission_lane) {
    addKeyValue('Submission Lane', SUBMISSION_LANE_LABELS[caseData.submission_lane as SubmissionLane] || caseData.submission_lane);
  }

  // ========== AUTHORIZATION RESULT ==========
  addSection('AUTHORIZATION RESULT');
  if (caseData.auth_number) {
    addKeyValue('Auth Number', caseData.auth_number);
    addKeyValue('Effective Start', safeFormatDate(caseData.auth_effective_start));
    addKeyValue('Effective End', safeFormatDate(caseData.auth_effective_end));
  } else {
    addKeyValue('Auth Number', 'Not yet authorized');
  }
  if (caseData.payer_normalized_status) {
    addKeyValue('Payer Status', NORMALIZED_STATUS_LABELS[caseData.payer_normalized_status as PayerNormalizedStatus] || caseData.payer_normalized_status);
  }
  if (caseData.payer_reference_number) {
    addKeyValue('Payer Reference', caseData.payer_reference_number);
  }
  if (caseData.denial_reason) {
    addKeyValue('Denial Reason', caseData.denial_reason);
  }
  if (caseData.denial_category) {
    addKeyValue('Denial Category', DENIAL_CATEGORY_LABELS[caseData.denial_category as DenialCategory] || caseData.denial_category);
  }

  // ========== OVERRIDE INFORMATION ==========
  if (overrideInfo) {
    addSection('OVERRIDE INFORMATION');
    doc.setTextColor(180, 83, 9); // amber-700
    addKeyValue('Override Applied', 'YES');
    addKeyValue('Override Reason', overrideInfo.reason);
    addKeyValue('Overridden By', overrideInfo.overriddenBy);
    addKeyValue('Overridden At', safeFormatDate(overrideInfo.overriddenAt, 'MMM d, yyyy h:mm a'));
    doc.setTextColor(0, 0, 0);
  }

  // ========== DOCUMENTS CHECKLIST ==========
  addSection('DOCUMENTS CHECKLIST');
  if (documents.length === 0) {
    addText('No documents associated with this case.', 9);
  } else {
    const completedDocs = documents.filter(d => d.is_uploaded).length;
    addText(`Completion: ${completedDocs}/${documents.length} documents uploaded`, 9, true);
    yPos += 3;
    
    documents.forEach(doc => {
      const status = doc.is_uploaded ? '✓' : '○';
      const required = doc.is_required ? ' [REQUIRED]' : '';
      const uploadedAt = doc.is_uploaded && doc.uploaded_at ? ` - Uploaded ${safeFormatDate(doc.uploaded_at)}` : '';
      addText(`${status} ${doc.document_name} (${doc.document_type})${required}${uploadedAt}`, 9);
    });
  }

  // ========== STATUS TIMELINE ==========
  if (includeFullTimeline && statusHistory.length > 0) {
    addSection('PAYER STATUS TIMELINE');
    statusHistory.forEach(entry => {
      const dateStr = format(entry.recordedAt, 'MMM d, yyyy h:mm a');
      addText(`[${dateStr}] ${NORMALIZED_STATUS_LABELS[entry.normalizedStatus]} (via ${entry.source})`, 9);
      if (entry.rawStatus) {
        addText(`   Raw: "${entry.rawStatus}"`, 8);
      }
      if (entry.notes) {
        addText(`   Notes: ${entry.notes}`, 8);
      }
    });
  }

  // ========== ACTIVITY LOG ==========
  if (includeFullTimeline) {
    addSection('ACTIVITY LOG');
    if (activityLog.length === 0) {
      addText('No activity recorded.', 9);
    } else {
      activityLog.forEach(entry => {
        const dateStr = safeFormatDate(entry.created_at, 'MMM d, yyyy h:mm a');
        addText(`[${dateStr}] ${entry.action}`, 9);
        addText(`   Actor: ${entry.actor_type}`, 8);
        if (entry.details && Object.keys(entry.details).length > 0) {
          addText(`   Details: ${JSON.stringify(entry.details)}`, 8);
        }
      });
    }
  }

  // ========== FOLLOW-UPS ==========
  if (followUps.length > 0) {
    addSection('FOLLOW-UPS');
    followUps.forEach(fu => {
      const status = fu.completed ? '✓' : '○';
      const dateStr = safeFormatDate(fu.scheduled_date, 'MMM d, yyyy h:mm a');
      addText(`${status} ${fu.follow_up_type} - ${dateStr}`, 9);
      if (fu.notes) {
        addText(`   Notes: ${fu.notes}`, 8);
      }
      if (fu.completed && fu.completed_at) {
        addText(`   Completed: ${safeFormatDate(fu.completed_at, 'MMM d, yyyy h:mm a')}`, 8);
      }
    });
  }

  // ========== DOCUMENT ATTACHMENTS LIST ==========
  if (includeDocumentLinks && documents.filter(d => d.is_uploaded && d.file_path).length > 0) {
    addSection('ATTACHED DOCUMENTS');
    addText('The following documents are included or referenced in this bundle:', 9);
    yPos += 2;
    
    const uploadedDocs = documents.filter(d => d.is_uploaded && d.file_path);
    uploadedDocs.forEach((doc, index) => {
      addText(`${index + 1}. ${doc.document_name} (${doc.document_type})`, 9);
      if (doc.file_path) {
        addText(`   Path: ${doc.file_path}`, 8);
      }
    });
  }

  // ========== FOOTER ==========
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFillColor(241, 245, 249); // slate-100
    doc.rect(0, doc.internal.pageSize.getHeight() - 15, pageWidth, 15, 'F');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139); // slate-500
    doc.text(
      `Generated by ${LINK_OPS_METADATA.generator} | Page ${i} of ${totalPages}`,
      margin,
      doc.internal.pageSize.getHeight() - 6
    );
    doc.text(
      format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      pageWidth - margin - 35,
      doc.internal.pageSize.getHeight() - 6
    );
  }

  return doc;
}

export function downloadEvidenceBundle(
  data: EvidenceBundleData,
  filename?: string
): void {
  const pdf = generateEvidenceBundlePDF(data);
  const defaultFilename = `evidence-bundle-${data.caseData.case_number}-${format(new Date(), 'yyyyMMdd-HHmmss')}.pdf`;
  pdf.save(filename || defaultFilename);
}

export function getEvidenceBundleBlob(data: EvidenceBundleData): Blob {
  const pdf = generateEvidenceBundlePDF(data);
  return pdf.output('blob');
}
